/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useTranslation } from 'react-i18next';
import { Button, Text } from '@/shared/ui';
import { cn } from '@/shared/ui/utils/tailwind';
import { ACTIVE_PIPELINE_STAGES } from '../constants/deal-stages';
import {
  ACCENT_SOFT_BG_CLASS,
  ACCENT_TEXT_CLASS,
  ACCENT_UNDERLINE_CLASS,
  type DealViewState,
} from '../utils/deal-state';

/** The five panels the detail screen can show below the action bar. */
export type DealDetailTab = 'summary' | 'proposals' | 'documents' | 'contacts' | 'checklist';

interface DealDetailTabsProps {
  active: DealDetailTab;
  onChange: (tab: DealDetailTab) => void;
  view: DealViewState;
  proposalCount: number;
  documentCount: number;
  contactCount: number;
  checklistDone: number;
  checklistTotal: number;
}

/**
 * Whether the closing checklist is worth showing yet. Before negotiation
 * it is all empty boxes about paperwork nobody has started, so the tab
 * only appears from negotiation onward — and stays for the record once
 * the deal is terminal.
 *
 * @param view - The deal's derived view state.
 * @returns True when the checklist tab should be offered.
 */
export function isChecklistTabAvailable(view: DealViewState): boolean {
  // The last funnel column, read off the list rather than hardcoded: the
  // pipeline went from five stages to four columns and a literal index
  // would have quietly started pointing at nothing.
  return view.lifecycle === 'terminal'
    || view.stageIndex >= ACTIVE_PIPELINE_STAGES.length - 1;
}

/** One tab's resolved label and count badge. */
interface TabSpec {
  id: DealDetailTab;
  label: string;
  badge: string;
}

/**
 * Builds the visible tab list, dropping the checklist when it does not
 * apply to the deal's current position.
 */
function useTabSpecs(props: DealDetailTabsProps): TabSpec[] {
  const { t } = useTranslation('features/deals');
  const specs: TabSpec[] = [
    { id: 'summary', label: t('detail.tabs.summary'), badge: '' },
    { id: 'proposals', label: t('detail.tabs.proposals'), badge: String(props.proposalCount) },
    { id: 'documents', label: t('detail.tabs.documents'), badge: String(props.documentCount) },
    { id: 'contacts', label: t('detail.tabs.contacts'), badge: String(props.contactCount) },
  ];
  if (isChecklistTabAvailable(props.view)) {
    specs.push({
      id: 'checklist',
      label: t('detail.tabs.checklist'),
      badge: `${props.checklistDone}/${props.checklistTotal}`,
    });
  }
  return specs;
}

/**
 * Tab strip for the deal detail screen. Underline marks the active panel
 * and each tab carries the count of what it holds, so the broker can see
 * an empty Propostas without opening it.
 *
 * @param props - The active tab, the change callback, and the counts.
 * @returns The tab strip.
 */
export function DealDetailTabs(props: DealDetailTabsProps) {
  const { active, onChange, view } = props;
  const specs = useTabSpecs(props);

  return (
    /*
      Sticky on a phone so the tabs stay reachable through a long tab body.
      `shrink-0` because a box declaring `overflow` inside a scrolling flex
      column otherwise shrinks to fit it rather than overflowing — which
      collapses the bar to its bottom border, silently.
    */
    <div
      role="tablist"
      data-testid="deal-detail-tabs"
      className="flex shrink-0 flex-nowrap items-center gap-0 overflow-x-auto border-b border-(--border-subtle) px-(--space-3) max-md:sticky max-md:top-0 max-md:z-10 max-md:min-h-11 max-md:bg-(--surface-primary)"
    >
      {specs.map((spec) => {
        const on = spec.id === active;
        return (
          <Button
            key={spec.id}
            role="tab"
            variant="none"
            aria-selected={on}
            onClick={() => onChange(spec.id)}
            data-testid={`deal-tab-${spec.id}`}
            className={cn(
              // `rounded-none` and `gap-0` on purpose: the `Button` base
              // rounds every corner at `--radius-md`, which curled the ends
              // of the 2px underline upward, and the gap cut it into
              // segments. The property and client detail tabs both draw one
              // straight rule across the strip; this is the same rule.
              'flex items-center gap-(--space-2) whitespace-nowrap border-b-2 bg-transparent',
              'shrink-0 rounded-none px-(--space-5) py-(--space-3)',
              'text-[length:var(--text-body-sm)]',
              on
                ? cn(
                  '[font-weight:var(--font-semibold)] text-(--text-primary)',
                  ACCENT_UNDERLINE_CLASS[view.accent],
                )
                : 'border-transparent [font-weight:var(--font-medium)] text-(--text-secondary)',
            )}
          >
            <Text component="span">{spec.label}</Text>
            {spec.badge && (
              <Text
                component="span"
                className={cn(
                  'rounded-full px-(--space-2) py-(--space-0-5)',
                  'text-[length:var(--text-tag)] [font-weight:var(--font-medium)] tabular-nums',
                  on
                    ? cn(ACCENT_SOFT_BG_CLASS[view.accent], ACCENT_TEXT_CLASS[view.accent])
                    : 'bg-(--surface-tertiary) text-(--text-tertiary-soft)',
                )}
              >
                {spec.badge}
              </Text>
            )}
          </Button>
        );
      })}
    </div>
  );
}
